import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  TextInput,
  FileInput,
  Select,
  SimpleGrid,
  Button,
  Paper,
  Title,
  Group,
} from "@mantine/core";
import classes from "./OperatorProfileForm.module.css";

const OperatorProfileForm = ({ data, onSubmit }) => {
  const [isEditing, setIsEditing] = useState(false);

  const { register, handleSubmit, control, reset } = useForm({
    defaultValues: {
      operator_type: data.operator_type || "",
      operator_name: data.operator_name || "",
      email: data.operator.email || "",
      phone_number: data.operator.phone_number || "",
      poc_name: data.poc_name || "",
      country: data.country || "",
      office_address: data.office_address || "",
      website: data.website || "",
      currency: data.currency || "",
      beneficiary_name: data.beneficiary_name || "",
      beneficiary_account_number: data.beneficiary_account_number || "",
      beneficiary_bank_name: data.beneficiary_bank_name || "",
      ifsc_code: data.ifsc_code || "",
      upi_id: data.upi_id || "",
      logo: data.logo || "",
      pan_card: data.pan_card || "",
      gst: data.gst || "",
      adhar_card: data.adhar_card || "",
      license: data.license || "",
    },
  });

  const handleEditToggle = () => setIsEditing(true);

  const handleCancel = () => {
    reset(); // reset to initial values
    setIsEditing(false);
  };

  const handleSave = (formData) => {
    onSubmit(formData);
    setIsEditing(false);
  };

  return (
    <form onSubmit={handleSubmit(handleSave)}>
      {/* Basic Details */}
      <Paper shadow="xl" className={classes.operatorProfilePaper}>
        <Group justify="space-between" align="center" px="md">
          <Title size="1rem" className={classes.heading}>Basic Details</Title>
          {!isEditing ? (
            <Button variant="outline" onClick={handleEditToggle}>
              Edit
            </Button>
          ) : null}
        </Group>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl" className={classes.operatorProfileForm}>
          <Controller
            name="operator_type"
            control={control}
            render={({ field }) => (
              <Select
                label="Type"
                data={["Domestic", "International"]}
                placeholder="Select operator type"
                disabled={!isEditing}
                {...field}
              />
            )}
          />

          <TextInput
            label="Name"
            placeholder="Enter operator name"
            disabled={!isEditing}
            {...register("operator_name")}
          />

          <TextInput
            label="Email"
            type="email"
            placeholder="Enter operator email"
            disabled={!isEditing}
            {...register("email")}
          />

          <TextInput
            label="Phone number"
            type="number"
            placeholder="Enter operator phone number"
            disabled={!isEditing}
            {...register("phone_number")}
          />

          <Controller
            name="logo"
            control={control}
            render={({ field }) => (
              <FileInput
                label="Upload Logo"
                placeholder="operator logo"
                onChange={field.onChange}
                disabled={!isEditing}
              />
            )}
          />

          <TextInput
            label="LIASE"
            placeholder="Enter name of LIASE"
            disabled={!isEditing}
            {...register("poc_name")}
          />

          <TextInput
            label="Country"
            placeholder="Enter country"
            disabled={!isEditing}
            {...register("country")}
          />

          <TextInput
            label="Office address"
            placeholder="Enter office address"
            disabled={!isEditing}
            {...register("office_address")}
          />

          <TextInput
            label="Website"
            placeholder="Enter operator website"
            disabled={!isEditing}
            {...register("website")}
          />

          <Controller
            name="currency"
            control={control}
            render={({ field }) => (
              <Select
                label="Payment currency"
                data={["INR", "USD", "NPR"]}
                placeholder="Enter payment currency"
                onChange={field.onChange}
                value={field.value}
                disabled={!isEditing}
              />
            )}
          />
        </SimpleGrid>
      </Paper>

      {/* Bank Details */}
      <Paper shadow="xl" my="xl" className={classes.operatorProfilePaper}>
        <Title size="1rem" className={classes.heading}>Bank Details</Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl" className={classes.operatorProfileForm}>
          <TextInput
            label="Beneficiary name"
            placeholder="Enter beneficiary name"
            disabled={!isEditing}
            {...register("beneficiary_name")}
          />

          <TextInput
            label="Beneficiary account"
            placeholder="Enter beneficiary account"
            disabled={!isEditing}
            {...register("beneficiary_account_number")}
          />

          <TextInput
            label="Beneficiary bank"
            placeholder="Enter beneficiary bank"
            disabled={!isEditing}
            {...register("beneficiary_bank_name")}
          />

          <TextInput
            label="Bank IFSC Code"
            placeholder="Enter IFSC code"
            disabled={!isEditing}
            {...register("ifsc_code")}
          />

          <TextInput
            label="UPI ID"
            placeholder="Enter UPI ID"
            disabled={!isEditing}
            {...register("upi_id")}
          />
        </SimpleGrid>
      </Paper>

      {/* Upload Documents */}
      <Paper shadow="xl" my="xl" className={classes.operatorProfilePaper}>
        <Title className={classes.heading}>Upload Documents</Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl" className={classes.operatorProfileForm}>
          <Controller
            name="pan_card"
            control={control}
            render={({ field }) => (
              <FileInput
                label="Pan Card"
                placeholder="operator pan card"
                onChange={field.onChange}
                disabled={!isEditing}
              />
            )}
          />

          <Controller
            name="gst"
            control={control}
            render={({ field }) => (
              <FileInput
                label="GST Document"
                placeholder="operator gst document"
                onChange={field.onChange}
                disabled={!isEditing}
              />
            )}
          />

          <Controller
            name="adhar_card"
            control={control}
            render={({ field }) => (
              <FileInput
                label="Aadhar Card"
                placeholder="operator aadhar document"
                onChange={field.onChange}
                disabled={!isEditing}
              />
            )}
          />

          <Controller
            name="license"
            control={control}
            render={({ field }) => (
              <FileInput
                label="License"
                placeholder="operator license"
                onChange={field.onChange}
                disabled={!isEditing}
              />
            )}
          />
        </SimpleGrid>

        {/* Submit button only in editing mode */}
        {/* {isEditing && (
          <Group justify="center">
            <Button type="submit" mt="xl" size="md">
              Submit
            </Button>
          </Group>
        )} */}
        <Group justify="center" mb="md" mt="lg">
          {isEditing ? (
            <>
              <Button type="submit" variant="filled">
                Submit
              </Button>
              <Button variant="outline" color="gray" onClick={handleCancel}>
                Cancel
              </Button>
            </>
          ) : null}
        </Group>
      </Paper>

      {/* Hidden Fields */}
      <TextInput type="hidden" value="Operators" {...register("designation")} />
      <TextInput type="hidden" value="Operators" {...register("permission")} />
      <TextInput type="hidden" value="Operators" {...register("department_name")} />
    </form>
  );
};

export default OperatorProfileForm;